import React, { useState } from 'react';
import { X, Save, Calendar, User, Tag, AlertTriangle } from 'lucide-react';
import { useRiskContext } from '../context/RiskContext.jsx';

export default function RiskDetailDrawer() {
  const [editTarget, setEditTarget] = useState('residual'); // 'initial' or 'residual'
  
  const {
    editingRisk,
    setEditingRisk,
    showRiskDetail,
    closeRiskDetail,
    handleEditRiskChange,
    saveRiskChanges,
    categories,
    riskActions,
    statusOptions,
    matrixConfig,
    getRiskPriority
  } = useRiskContext();

  if (!showRiskDetail || !editingRisk) {
    return null;
  }

  const priority = getRiskPriority(editingRisk.likelihood, editingRisk.impact);
  const initialBoxPriority = getRiskPriority(
    editingRisk.initialLikelihood || editingRisk.likelihood,
    editingRisk.initialImpact || editingRisk.impact
  );
  const residualBoxPriority = getRiskPriority(editingRisk.likelihood, editingRisk.impact);

  const handleMatrixSelect = (likelihood, impact) => {
    console.log(`Setting ${editTarget} - likelihood: ${likelihood}, impact: ${impact}`);
    
    if (editTarget === 'initial') {
      // Update initial values
      const updatedRisk = { 
        ...editingRisk, 
        initialLikelihood: likelihood, 
        initialImpact: impact 
      };
      console.log('Updated risk with initial values:', updatedRisk);
      setEditingRisk(updatedRisk);
    } else {
      // Update residual values (current likelihood/impact)
      const updatedRisk = { 
        ...editingRisk, 
        likelihood: likelihood, 
        impact: impact 
      };
      console.log('Updated risk with residual values:', updatedRisk);
      setEditingRisk(updatedRisk);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" style={{zIndex: 9999}}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl rounded-lg">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">Risk Details</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                console.log('Saving risk changes. Current editingRisk:', editingRisk);
                saveRiskChanges();
              }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <Save size={14} />
              Save
            </button>
            <button
              onClick={closeRiskDetail}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 grid gap-6 md:grid-cols-5 overflow-y-auto max-h-[80vh]">

          {/* LEFT COLUMN – meta & action plan */}
          <div className="md:col-span-2 space-y-4">
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={editingRisk.description}
                onChange={(e) => handleEditRiskChange('description', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe the risk..."
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Tag size={14} />
                Category
              </label>
              <select
                value={editingRisk.category}
                onChange={(e) => handleEditRiskChange('category', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={editingRisk.status}
                onChange={(e) => handleEditRiskChange('status', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <User size={14} />
                Action Owner
              </label>
              <input
                type="text"
                value={editingRisk.actionOwner}
                onChange={(e) => handleEditRiskChange('actionOwner', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Who is responsible?"
              />
            </div>

            {/* Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar size={14} />
                Deadline
              </label>
              <input
                type="date"
                value={editingRisk.deadline}
                onChange={(e) => handleEditRiskChange('deadline', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Risk Response Action */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk Response Action
              </label>
              <select
                value={editingRisk.action}
                onChange={(e) => handleEditRiskChange('action', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {riskActions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>

            {/* Action Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Description
              </label>
              <textarea
                value={editingRisk.actionDescription}
                onChange={(e) => handleEditRiskChange('actionDescription', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe the action plan..."
              />
            </div>
          </div>

          {/* RIGHT COLUMN – risk assessment matrices */}
          <div className="md:col-span-3 space-y-4">
            {/* Toggle & current scores */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-6 mb-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Edit which risk score?</span>
                {[
                  { value: 'initial', label: 'Initial Risk' },
                  { value: 'residual', label: 'Residual Risk' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="radio"
                      className="accent-blue-600"
                      checked={editTarget === option.value}
                      onChange={() => setEditTarget(option.value)}
                    />
                    <span className={editTarget === option.value ? 'font-medium text-blue-700' : 'text-gray-600'}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>

              {/* Current values display */}
              <div className="grid grid-cols-2 gap-4 text-xs">
                {/* Initial */}
                <div className="p-2 rounded border text-white" style={{backgroundColor: initialBoxPriority.color}}>
                  <div className="font-medium text-gray-600 mb-1">Initial Risk</div>
                  <div>
                    L: {(() => {
                      const val = editingRisk.initialLikelihood || editingRisk.likelihood;
                      const levels = matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || [];
                      return levels[val - 1] || val;
                    })()} |
                    I: {(() => {
                      const val = editingRisk.initialImpact || editingRisk.impact;
                      const levels = matrixConfig.impactLevels || [];
                      return levels[val - 1] || val;
                    })()}
                  </div>
                </div>
                {/* Residual */}
                <div className="p-2 rounded border text-white" style={{backgroundColor: residualBoxPriority.color}}>
                  <div className="font-medium text-gray-600 mb-1">Residual Risk</div>
                  <div>
                    L: {(() => {
                      const levels = matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || [];
                      return levels[editingRisk.likelihood - 1] || editingRisk.likelihood;
                    })()} |
                    I: {(() => {
                      const levels = matrixConfig.impactLevels || [];
                      return levels[editingRisk.impact - 1] || editingRisk.impact;
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Risk Matrix */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3">
                Risk Matrix – Editing {editTarget === 'initial' ? 'Initial' : 'Residual'} Risk
              </h4>
              <div className="relative">
                {/* Likelihood labels (top) */}
                <div className={`grid gap-1 mb-1`} style={{gridTemplateColumns:`repeat(${matrixConfig.impactLevels.length+1}, minmax(0,1fr))`}}>
                  <div></div>{/* Empty corner */}
                  {matrixConfig.likelihoodLevels.map((level, i) => (
                    <div key={i} className="text-xs text-center text-gray-600 font-medium px-1">
                      {level}
                    </div>
                  ))}
                </div>

                {/* Matrix rows with impact labels */}
                {Array.from({ length: matrixConfig.impactLevels.length }, (_, row) => (
                  <div key={row} className="grid gap-1 mb-1" style={{gridTemplateColumns:`repeat(${matrixConfig.likelihoodLevels.length+1}, minmax(0,1fr))`}}>
                    {/* Impact label (left) */}
                    <div className="text-xs text-gray-600 font-medium flex items-center justify-center">
                      {matrixConfig.impactLevels[matrixConfig.impactLevels.length-1 - row]}
                    </div>
                    {/* Matrix cells */}
                    {Array.from({ length: matrixConfig.likelihoodLevels.length }, (_, col) => {
                      const impact = matrixConfig.impactLevels.length - row;
                      const likelihood = col + 1;

                      const isResidualRisk = likelihood === editingRisk.likelihood && impact === editingRisk.impact;
                      const isInitialRisk = likelihood === (editingRisk.initialLikelihood || editingRisk.likelihood) && impact === (editingRisk.initialImpact || editingRisk.impact);
                      const isBeingEdited = editTarget === 'residual' ? isResidualRisk : isInitialRisk;
                      const cellPriority = getRiskPriority(likelihood, impact);

                      return (
                        <div
                          key={col}
                          className={`aspect-square relative flex items-center justify-center rounded border-2 cursor-pointer hover:border-blue-500 transition-all ${isBeingEdited ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-300 hover:border-gray-400'}`}
                          style={{ backgroundColor: cellPriority.color, opacity: 0.8 }}
                          onClick={() => handleMatrixSelect(likelihood, impact)}
                          title={`Set ${editTarget} - Likelihood: ${matrixConfig.likelihoodLevels[likelihood - 1]}, Impact: ${matrixConfig.impactLevels[impact - 1]}`}
                        >
                          {isResidualRisk && (
                            <div className="absolute w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow-md z-10" title="Residual Risk Position"></div>
                          )}
                          {isInitialRisk && !isResidualRisk && (
                            <div className="absolute w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-md z-10" title="Initial Risk Position"></div>
                          )}
                          {isInitialRisk && isResidualRisk && (
                            <div className="absolute w-4 h-4 rounded-full bg-gradient-to-br from-red-500 to-blue-600 border-2 border-white shadow-md z-10" title="Initial & Residual Risk Position (Same)"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center text-xs text-gray-600">
                  <span className="font-medium">Likelihood →</span>
                  <span className="font-medium">↑ Impact</span>
                </div>
                <div className="flex items-center justify-center gap-4 text-xs text-gray-600 bg-white p-2 rounded border">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-500 border border-white"></div>
                    <span>Initial Risk</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-blue-600 border border-white"></div>
                    <span>Residual Risk</span>
                  </div>
                </div>
              </div>

              <div className="text-xs text-gray-500 mt-2 text-center italic">
                Click on any cell to set {editTarget} risk position
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER ACTION BAR */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-4">
          <button
            onClick={closeRiskDetail}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => saveRiskChanges()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
} 